import { hexToRgb, valueToHeatColor } from './heatmapColor';
import { renderMatrixHeatmap, heatColor } from './heatmapMatrix';
import { IMAGE_DENSITY_MARKER_LIMIT, aggregateMarkerValue, coalescePointsForMarkers } from './imageDensityPoints';

/** Radio en píxeles de pantalla. No depende de la resolución de la foto. */
const MARKER_SIZE_RADIUS = {
  small: 12,
  medium: 16,
  large: 26,
};

function markerRadiusForSize(size) {
  return MARKER_SIZE_RADIUS[size] || MARKER_SIZE_RADIUS.medium;
}

function clusterMemberCount(p) {
  return p?.count != null && Number.isFinite(p.count) ? p.count : 1;
}

function drawClusterMarkers(ctx, points, { scaleX, scaleY, clusterRadiusPx, overlayOpacity }) {
  if (!points.length) return;
  const maxCount = Math.max(1, ...points.map(clusterMemberCount));
  const scale = Math.min(scaleX, scaleY);
  const baseR = Math.max(8, clusterRadiusPx * scale * 0.55);
  const opacityScale = overlayOpacity != null ? overlayOpacity : 1;

  for (const p of points) {
    const cx = p.x * scaleX;
    const cy = p.y * scaleY;
    const t = clusterMemberCount(p) / maxCount;
    const [r, g, b, aNorm] = heatColor(t);
    const alpha = opacityScale * aNorm;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, baseR);
    grad.addColorStop(0, `rgba(${r},${g},${b},${Math.min(0.92, alpha + 0.12)})`);
    grad.addColorStop(0.65, `rgba(${r},${g},${b},${alpha * 0.78})`);
    grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, baseR, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawClusterCountLabels(ctx, points, { scaleX, scaleY, clusterRadiusPx }) {
  if (!points.length) return;
  const scale = Math.min(scaleX, scaleY);
  const fontSize = Math.max(10, Math.min(18, clusterRadiusPx * scale * 0.38));
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `600 ${fontSize}px system-ui, sans-serif`;
  for (const p of points) {
    const cx = p.x * scaleX;
    const cy = p.y * scaleY;
    const label = String(clusterMemberCount(p));
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.strokeText(label, cx, cy);
    ctx.fillStyle = 'rgba(18,18,18,0.92)';
    ctx.fillText(label, cx, cy);
  }
  ctx.restore();
}

function drawPointsLayer(ctx, points, { pointRadius, scaleX, scaleY, heatmapConfig }) {
  const color = hexToRgb(heatmapConfig.colorHigh || '#e53935');
  const alpha = heatmapConfig.colorOpacity != null ? heatmapConfig.colorOpacity : 0.35;
  const r = Math.max(3, pointRadius);
  for (const p of points) {
    const cx = p.x * scaleX;
    const cy = p.y * scaleY;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0, `rgba(${color.r},${color.g},${color.b},${Math.min(0.85, alpha + 0.25)})`);
    grad.addColorStop(1, `rgba(${color.r},${color.g},${color.b},0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function fillRoundRect(ctx, x, y, w, h, radius) {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, radius);
  else ctx.rect(x, y, w, h);
  ctx.fill();
}

/** Cabeza circular y cola tangente. La punta queda debajo del punto. */
function pinLayout(cy, r) {
  const headR = r * 0.8;
  const headY = cy - r * 0.32;
  const tipY = cy + r * 1.18;
  return { headR, headY, tipY };
}

function traceMarkerShape(ctx, shape, cx, cy, r) {
  ctx.beginPath();
  if (shape === 'square') {
    ctx.rect(cx - r, cy - r, r * 2, r * 2);
    return;
  }
  if (shape === 'diamond') {
    ctx.moveTo(cx, cy - r * 1.2);
    ctx.lineTo(cx + r * 1.15, cy);
    ctx.lineTo(cx, cy + r * 1.2);
    ctx.lineTo(cx - r * 1.15, cy);
    ctx.closePath();
    return;
  }
  if (shape === 'pin') {
    const { headR, headY, tipY } = pinLayout(cy, r);
    const dist = Math.max(tipY - headY, headR + 0.01);
    const alpha = Math.asin(Math.min(0.95, headR / dist));
    const down = Math.PI / 2;
    ctx.arc(cx, headY, headR, down + alpha, down - alpha, false);
    ctx.lineTo(cx, tipY);
    ctx.closePath();
    return;
  }
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
}

function markerCellText(row, column) {
  if (!column || !row || row[column] == null || row[column] === '') return '';
  return String(row[column]);
}

function contentFactors(shape) {
  if (shape === 'square') return { w: 1.55, h: 1.35 };
  if (shape === 'diamond') return { w: 1.05, h: 0.82 };
  if (shape === 'pin') return { w: 1.15, h: 0.95 };
  return { w: 1.32, h: 1.02 };
}

function fontSizeForBox(ctx, text, boxW, boxH) {
  const cap = Math.max(12, Math.min(28, boxH * 0.72));
  if (!text) return cap;
  ctx.font = `700 ${cap}px system-ui, sans-serif`;
  const width = ctx.measureText(text).width || 1;
  const fitted = width > boxW ? cap * (boxW / width) : cap;
  return Math.min(cap, fitted);
}

function radiusForText(ctx, text, baseR, shape) {
  if (!text) return baseR;
  const font = Math.max(13, Math.min(22, baseR * 0.55));
  ctx.font = `700 ${font}px system-ui, sans-serif`;
  const width = ctx.measureText(text).width;
  const factors = contentFactors(shape);
  const fromWidth = (width + 12) / factors.w;
  const fromHeight = (font + 10) / factors.h;
  return Math.min(Math.max(baseR, fromWidth, fromHeight), Math.max(baseR * 3.5, 64));
}

function inkForFill(hex) {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#1a1a1a' : '#ffffff';
}

function drawStyledMarkers(
  ctx,
  points,
  {
    scaleX,
    scaleY,
    pointRadius,
    markerSize,
    shape,
    valueColumn,
    valueAggregation = 'first',
    labelColumn,
    fillMode,
    fitMode,
    solidColor,
    heatmapConfig,
  }
) {
  if (!points.length) return pointRadius || 14;
  const coalesced = coalescePointsForMarkers(points);
  const drawn = coalesced.slice(0, IMAGE_DENSITY_MARKER_LIMIT);
  const numeric = drawn
    .map((p) => aggregateMarkerValue(p, valueColumn, valueAggregation).numeric)
    .filter((n) => Number.isFinite(n));
  const min = numeric.length ? Math.min(...numeric) : 0;
  const max = numeric.length ? Math.max(...numeric) : 1;
  const span = max - min || 1;
  const baseR = markerRadiusForSize(markerSize);
  const markerShape = shape || 'circle';
  const growMarker = fitMode === 'marker';
  const colored = fillMode !== 'none';
  const solid = colored ? '#ffffff' : solidColor || '#ffffff';
  const solidInk = inkForFill(solid);
  let maxRadius = baseR;

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const p of drawn) {
    const cx = p.x * scaleX;
    const cy = p.y * scaleY;
    const aggregated = aggregateMarkerValue(p, valueColumn, valueAggregation);
    const raw = aggregated.numeric;
    const t = Number.isFinite(raw) ? (raw - min) / span : 0.5;
    const fill = colored ? valueToHeatColor(t, heatmapConfig) : solid;
    const inner = aggregated.text;
    const outer = markerCellText(p.row, labelColumn);
    const r = growMarker ? radiusForText(ctx, inner, baseR, markerShape) : baseR;
    maxRadius = Math.max(maxRadius, r);
    const pin = markerShape === 'pin' ? pinLayout(cy, r) : null;
    const textY = pin ? pin.headY : cy;
    const bottom = pin ? pin.tipY : cy + r;
    if (pin) maxRadius = Math.max(maxRadius, r * 1.25);

    traceMarkerShape(ctx, markerShape, cx, cy, r);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.lineJoin = pin ? 'round' : 'miter';
    ctx.strokeStyle = colored || solidInk === '#ffffff' ? 'rgba(255,255,255,0.95)' : 'rgba(40,40,40,0.8)';
    ctx.stroke();

    if (inner) {
      const box = contentFactors(markerShape);
      const fontSize = fontSizeForBox(ctx, inner, r * box.w, r * box.h);
      ctx.font = `700 ${fontSize}px system-ui, sans-serif`;
      ctx.fillStyle = colored ? '#ffffff' : solidInk;
      ctx.fillText(inner, cx, textY);
    }

    if (outer) {
      const labelFont = Math.round(Math.max(12, Math.min(18, r * 0.48)));
      const labelH = labelFont + 6;
      ctx.font = `${labelFont}px system-ui, sans-serif`;
      const width = Math.min(260, ctx.measureText(outer).width + 12);
      const labelY = bottom + 8;
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      fillRoundRect(ctx, cx - width / 2, labelY, width, labelH, 3);
      ctx.save();
      ctx.beginPath();
      ctx.rect(cx - width / 2, labelY, width, labelH);
      ctx.clip();
      ctx.fillStyle = '#1a1a1a';
      ctx.font = `${labelFont}px system-ui, sans-serif`;
      ctx.fillText(outer, cx, labelY + labelH / 2);
      ctx.restore();
    }
  }
  ctx.restore();
  return maxRadius;
}

function drawLabeledMarkers(ctx, points, { scaleX, scaleY, pointRadius, selectedKey }) {
  if (!points.length) return;
  const drawn = [];
  const seen = new Set();
  for (const p of points) {
    const key = p.positionKey ? String(p.positionKey) : '';
    const dedupe = key || `__xy:${p.x}:${p.y}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    drawn.push(p);
  }
  const r = Math.max(10, pointRadius || 14);
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const fontSize = Math.max(9, Math.min(13, r * 0.85));
  ctx.font = `700 ${fontSize}px system-ui, sans-serif`;
  drawn.forEach((p, index) => {
    const cx = p.x * scaleX;
    const cy = p.y * scaleY;
    const label = p.markerNumber != null ? String(p.markerNumber) : String(index + 1);
    const selected = selectedKey && (p.positionKey === selectedKey || p.label === selectedKey);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = selected ? '#1565c0' : '#e53935';
    ctx.fill();
    ctx.lineWidth = selected ? 3 : 2;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.strokeText(label, cx, cy);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, cx, cy);
  });
  ctx.restore();
}

function drawSelectedHighlight(ctx, point, { scaleX, scaleY, hitRadius }) {
  if (!point) return;
  const cx = point.x * scaleX;
  const cy = point.y * scaleY;
  const r = Math.max(10, hitRadius * 0.85);
  ctx.save();
  ctx.strokeStyle = '#1976d2';
  ctx.lineWidth = 3;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = 'rgba(25, 118, 210, 0.12)';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Dibuja el heatmap/puntos sobre imagen y devuelve layout para hit-testing.
 */
export function drawImageDensityCanvas(
  ctx,
  canvasW,
  canvasH,
  {
    img,
    imgReady,
    url,
    naturalW,
    naturalH,
    points,
    selectedPoint,
    renderMode,
    heatmapStyle,
    intensity,
    overlayOpacity,
    blur,
    pointSize,
    separation,
    backgroundDim,
    pointRadius,
    heatmapConfig,
    clusterEnabled,
    clusterRadiusPx,
    clusterShowCount,
    clusterColorByCount,
    showMarkerLabels = false,
    editMode = false,
    selectedPositionKey = '',
    markerShape = 'circle',
    markerValueColumn = '',
    markerValueAggregation = 'first',
    markerLabelColumn = '',
    markerFill = 'value',
    markerFit = 'text',
    markerSize = 'medium',
    markerColor = '#ffffff',
  }
) {
  ctx.clearRect(0, 0, canvasW, canvasH);

  if (img && imgReady && img.naturalWidth > 0) {
    ctx.globalAlpha = backgroundDim != null ? backgroundDim : 0.72;
    ctx.drawImage(img, 0, 0, canvasW, canvasH);
    ctx.globalAlpha = 1;
  } else if (!url) {
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, canvasW, canvasH);
  } else if (imgReady) {
    ctx.fillStyle = '#e8e8e8';
    ctx.fillRect(0, 0, canvasW, canvasH);
  } else {
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, canvasW, canvasH);
  }

  const sx = canvasW / Math.max(1, naturalW);
  const sy = canvasH / Math.max(1, naturalH);
  const scale = Math.min(canvasW / Math.max(1, naturalW), canvasH / Math.max(1, naturalH));
  const useGlobitos = editMode || (showMarkerLabels && renderMode !== 'markers');
  let hitRadius = useGlobitos
    ? Math.max(10, pointRadius * scale)
    : renderMode === 'markers'
      ? Math.max(10, pointRadius * scale)
      : renderMode === 'points'
        ? Math.max(6, pointRadius * scale)
        : Math.max(8, (clusterEnabled ? clusterRadiusPx : 12) * scale);

  if (!points.length) {
    return { scaleX: sx, scaleY: sy, hitRadius, naturalW, naturalH, points: [] };
  }

  const skipHeatOverlay = editMode && useGlobitos;

  if (!skipHeatOverlay && renderMode === 'heatmap') {
    if (clusterEnabled && clusterColorByCount) {
      drawClusterMarkers(ctx, points, {
        scaleX: sx,
        scaleY: sy,
        clusterRadiusPx,
        overlayOpacity,
      });
    } else {
      renderMatrixHeatmap(
        ctx,
        points.map((p) => ({ x: p.x, y: p.y, weight: p.weight })),
        naturalW,
        naturalH,
        canvasW,
        canvasH,
        {
          heatmapStyle,
          intensity,
          overlayOpacity,
          blur,
          pointSize,
          separation,
        }
      );
    }
    if (clusterEnabled && clusterShowCount) {
      drawClusterCountLabels(ctx, points, {
        scaleX: sx,
        scaleY: sy,
        clusterRadiusPx,
      });
    }
  } else if (!skipHeatOverlay && renderMode === 'markers') {
    const usedRadius = drawStyledMarkers(ctx, points, {
      scaleX: sx,
      scaleY: sy,
      pointRadius: markerRadiusForSize(markerSize),
      markerSize,
      shape: markerShape,
      valueColumn: markerValueColumn,
      valueAggregation: markerValueAggregation,
      labelColumn: markerLabelColumn,
      fillMode: markerFill,
      fitMode: markerFit,
      solidColor: markerColor,
      heatmapConfig,
    });
    hitRadius = Math.max(hitRadius, usedRadius || 0);
  } else if (!skipHeatOverlay && clusterEnabled && clusterColorByCount) {
    drawClusterMarkers(ctx, points, {
      scaleX: sx,
      scaleY: sy,
      clusterRadiusPx,
      overlayOpacity,
    });
    if (clusterEnabled && clusterShowCount) {
      drawClusterCountLabels(ctx, points, {
        scaleX: sx,
        scaleY: sy,
        clusterRadiusPx,
      });
    }
  } else if (!skipHeatOverlay && !useGlobitos) {
    drawPointsLayer(ctx, points, {
      pointRadius: Math.max(4, pointRadius * scale),
      scaleX: sx,
      scaleY: sy,
      heatmapConfig,
    });
    if (clusterEnabled && clusterShowCount) {
      drawClusterCountLabels(ctx, points, {
        scaleX: sx,
        scaleY: sy,
        clusterRadiusPx,
      });
    }
  }

  if (useGlobitos) {
    drawLabeledMarkers(ctx, points, {
      scaleX: sx,
      scaleY: sy,
      pointRadius: Math.max(10, pointRadius * scale),
      selectedKey: selectedPositionKey,
    });
  }

  if (!editMode) {
    drawSelectedHighlight(ctx, selectedPoint, { scaleX: sx, scaleY: sy, hitRadius });
  }

  const pointsForHit = points.map((p) => ({
    ...p,
    _cx: p.x * sx,
    _cy: p.y * sy,
  }));

  return {
    scaleX: sx,
    scaleY: sy,
    hitRadius,
    naturalW,
    naturalH,
    points: pointsForHit,
  };
}
